import {Box, Typography} from '@mui/material';
import {t} from 'i18next';
import {Can} from '@casl/react';
import styles from '../../../styles/components/clientdetails/projectDrawerStyle.module.scss';
import {IProjectDetails} from '../../../types/project-type/projectDetailsType';
import AurHighlyConfidential from '../../common/aur-highly-confidential/AurHighlyConfidential';
import {IconColors, IconNames} from '../../common/aur-icon/AurIcon';
import AurPill from '../../common/aur-pill/AurPill';
import AurCountryFlag from '../../common/aur-country-flag/AurCountryFlag';
import {AurSwitch} from '../../common/aur-switch/AurSwitch';
import {useContext, useEffect, useState} from 'react';
import {AuthorizationContext} from '../../../contexts/authorizationContext/AuthorizationContext';
import {Permissions, Resources} from '../../../utils/authorization';
import {useSelector} from 'react-redux';
import {RootState} from '../../../redux/store';
import {
  getProjectRealtimeBudget,
  updateBudgetConsumption,
} from '../../../api/ClientManagementService';
import {AurLightTooltip} from '../../common/aur-light-tooltip/aur-light-tooltip';
import {getUTCDateTimeForDisplay} from '../../../utils/utils';
import {ONLY_DATE_SHOW_FORMAT} from '../../../constants/constants';

type ProjectInfoSectionProps = {
  ProjectInfo: IProjectDetails | null;
};

const ProjectInfoSection = ({ProjectInfo}: ProjectInfoSectionProps) => {
  const i = useContext(AuthorizationContext);

  const clientId = useSelector(
    (state: RootState) => state.clientList.selectedClientId
  );
  const selectedProjectId = useSelector(
    (state: RootState) => state.department.selectedProjectId
  );

  const [showBudgetConsumption, setShowBudgetConsumption] = useState(
    ProjectInfo ? ProjectInfo.budgetConsumptionEnabled : false
  );
  const [showBudgetConsumptionDisabled, setShowBudgetConsumptionDisabled] =
    useState(ProjectInfo?.projectStatus.toLowerCase() != 'active');
  const [showBudgetConsumptionToggle, setShowBudgetConsumptionToggle] =
    useState(false);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await getProjectRealtimeBudget(
          clientId,
          selectedProjectId
        );
        const res = response.data;
        const resData = res?.data;
        const budgetEnabled =
          ProjectInfo?.projectStatus.toLowerCase() === 'active' &&
          ProjectInfo.budgetConsumptionEnabled === true;
        setShowBudgetConsumptionDisabled(
          !budgetEnabled &&
            (Number.parseFloat(resData?.budgetLimit || 0) <= 0 ||
              ProjectInfo?.projectStatus.toLowerCase() != 'active')
        );
        setShowBudgetConsumptionToggle(true);
      } catch (error) {
        console.log(error);
        setShowBudgetConsumptionDisabled(true);
        setShowBudgetConsumptionToggle(true);
      }
    };
    if (clientId && selectedProjectId) {
      fetchData();
    }
  }, [clientId, selectedProjectId]);

  const changeBudgetConsumption = (budgetConsumptionEnabled: boolean) => {
    if (clientId && selectedProjectId) {
      try {
        updateBudgetConsumption(
          clientId,
          selectedProjectId,
          budgetConsumptionEnabled
        );
      } catch (error) {
        console.log(error);
      }
    }
  };

  const projectStatus = () => {
    let body: React.ReactNode = null;
    if (ProjectInfo?.projectStatus) {
      if (
        ProjectInfo?.projectStatus.toLowerCase() === 'active' ||
        ProjectInfo?.projectStatus.toLowerCase() === 'ongoing'
      ) {
        body = (
          <AurPill
            label={t('common.projectStatus.' + ProjectInfo?.projectStatus)}
            icon={IconNames.Circle}
            iconSize="small"
            iconColor={IconColors.Success}
          />
        );
      } else {
        body = (
          <AurPill
            label={t('common.projectStatus.' + ProjectInfo?.projectStatus)}
            icon={IconNames.Circle}
            iconSize="small"
            iconColor={IconColors.Critical}
          />
        );
      }
    }

    return body;
  };
  const projectSubTitle = () => {
    let body: React.ReactNode = null;
    if (
      ProjectInfo?.clientName &&
      ProjectInfo?.branchName &&
      ProjectInfo?.departmentName
    ) {
      body = (
        <>{`${ProjectInfo?.clientName}. ${ProjectInfo?.branchName}. ${ProjectInfo?.departmentName}`}</>
      );
    }
    return body;
  };
  return (
    <>
      <Box className={`${styles.projectTitleSection}`}>
        {ProjectInfo?.highlyConfidential && (
          <AurHighlyConfidential
            type="pill"
            className={`${styles.projectHighlyConfidential}`}
          />
        )}
        <Box className={`typo-text display2_size ${styles.projectTitle}`}>
          {ProjectInfo?.projectName}
        </Box>
        <Box>
          <Box className={`typo-text label4_size ${styles.subTitleLabel}`}>
            {t('clientDetailsPage.projectDrawer.clientLabel')}.{' '}
            {t('clientDetailsPage.projectDrawer.branchLabel')}.{' '}
            {t('clientDetailsPage.projectDrawer.deptLabel')}
          </Box>
          <Box className={`typo-text body1_size ${styles.subTitleText}`}>
            {projectSubTitle()}
          </Box>
        </Box>
      </Box>
      <Box className={`${styles.projectStatusSection}`}>
        <Box className={`${styles.infoSection}`}>
          <Typography
            className={`typo-text subhead5_size ${styles.statusSectionLabe1}`}
          >
            {t('clientDetailsPage.projectDrawer.projectStatus')}
          </Typography>
          <Box sx={{paddingLeft: '10px'}}>{projectStatus()}</Box>
        </Box>
        <Box className={`${styles.infoSection}`}>
          <Typography className={`${styles.infoSectionLabel}`}>
            {t('clientDetailsPage.projectDrawer.creatdOn')}
          </Typography>
          <AurLightTooltip title={t('common.yearMonthDate')}>
            <Typography className={`${styles.infoSectionLabel}`}>
              {ProjectInfo?.createdOn
                ? getUTCDateTimeForDisplay(
                    ProjectInfo?.createdOn,
                    ONLY_DATE_SHOW_FORMAT
                  )
                : '/'}
            </Typography>
          </AurLightTooltip>
        </Box>
      </Box>
      <Box className={`${styles.projectDetailsInfoSection}`}>
        {/* Left Column */}
        <Box className={`${styles.infoBoxLeft}`}>
          <Box className={`${styles.infoSection}`}>
            <Typography
              className={`typo-text subhead4_size ${styles.infoSectionLabel}`}
            >
              {t('clientDetailsPage.projectDrawer.projectCode')}
            </Typography>
            <Typography
              className={`typo-text body2_size ${styles.infoSectionData} `}
            >
              {ProjectInfo?.projectCode}
            </Typography>
          </Box>
          <Box className={`${styles.infoSection}`}>
            <Typography
              className={`typo-text subhead4_size ${styles.infoSectionLabel}`}
            >
              {t('clientDetailsPage.projectDrawer.clientPreferredID')}
            </Typography>
            <Typography
              className={`typo-text body2_size ${styles.infoSectionData} `}
            >
              {ProjectInfo?.clientPreferredId}
            </Typography>
          </Box>
          <Box className={`${styles.infoSection}`}>
            <Typography
              className={`typo-text subhead4_size ${styles.infoSectionLabel}`}
            >
              {t('clientDetailsPage.projectDrawer.vertical')}
            </Typography>
            <Typography
              className={`typo-text body2_size ${styles.infoSectionData} `}
            >
              {ProjectInfo?.vertical}
            </Typography>
          </Box>
          <Box className={`${styles.infoSection}`}>
            <Typography
              className={`typo-text subhead4_size ${styles.infoSectionLabel}`}
            >
              {t('clientDetailsPage.projectDrawer.sensitivity')}
            </Typography>
            <Typography
              className={`typo-text body2_size ${styles.infoSectionData} `}
            >
              {ProjectInfo?.sensitivity}
            </Typography>
          </Box>
          <Box className={`${styles.infoSection}`}>
            <Typography
              className={`typo-text subhead4_size ${styles.infoSectionLabel1}`}
            >
              {t('common.projectServiceType')}
            </Typography>
            <Typography
              className={`typo-text body2_size ${styles.infoSectionData1} `}
            >
              {ProjectInfo?.auroraType || t('common.notSpecified')}
            </Typography>
          </Box>
        </Box>

        {/* Right Column */}
        <Box className={`${styles.infoBoxRight}`}>
          <Box className={`${styles.infoSection}`}>
            <Typography
              className={`typo-text subhead4_size ${styles.infoSectionLabel}`}
            >
              {t('clientDetailsPage.projectDrawer.projectType')}
            </Typography>
            <Typography
              className={`typo-text body2_size ${styles.infoSectionData} `}
            >
              {ProjectInfo?.projectType}
            </Typography>
          </Box>
          <Box className={`${styles.infoSection}`}>
            <Typography
              className={`typo-text subhead4_size ${styles.infoSectionLabel}`}
            >
              {t('clientDetailsPage.projectDrawer.mSA')}
            </Typography>
            <Typography
              className={`typo-text body2_size ${styles.infoSectionData} `}
            >
              {ProjectInfo?.isMSA === 'False' ? 'No' : 'Yes'}
            </Typography>
          </Box>
          <Box className={`${styles.infoSection}`}>
            <Typography
              className={`typo-text subhead4_size  ${styles.infoSectionLabel}`}
            >
              {t('clientDetailsPage.projectDrawer.accountRepresentative')}
            </Typography>
            <Typography
              className={`typo-text body2_size ${styles.infoSectionData}`}
            >
              {ProjectInfo?.accountRepresentative}
            </Typography>
          </Box>

          <Box className={`${styles.infoSection}`}>
            <Typography
              className={`typo-text subhead4_size  ${styles.infoSectionLabel}`}
            >
              {t('common.hostingLocation')}
            </Typography>
            <Typography
              className={`typo-text body2_size ${styles.infoSectionData}`}
            >
              {ProjectInfo?.countryName ? (
                <>
                  <AurCountryFlag
                    countryCode={ProjectInfo?.domainCountryCode ?? ''}
                    altText={ProjectInfo?.domainCountryCode ?? ''}
                  />
                  <Box component="span" className="margin-left-5">
                    {ProjectInfo?.countryName ?? ''}
                  </Box>
                </>
              ) : (
                '--'
              )}
            </Typography>
          </Box>

          <Box className={`${styles.infoSection}`}>
            <Typography
              className={`typo-text subhead4_size  ${styles.infoSectionLabel}`}
            >
              {t('common.dataCenter')}
            </Typography>
            <Typography
              className={`typo-text body2_size ${styles.infoSectionData}`}
            >
              {ProjectInfo?.dataCenterName || '--'}
            </Typography>
          </Box>
          <Can
            I={Permissions.View}
            a={Resources.Project_EstimatedBudgetConsumption}
            ability={i}
          >
            {showBudgetConsumptionToggle ? (
              <Box className={`${styles.infoSection}`}>
                <Typography
                  className={`typo-text subhead4_size  ${styles.infoSectionLabel}`}
                >
                  {t('common.showEstimatedBudgetConsumption')}
                </Typography>
                <Box className={`${styles.switchWrapper}`}>
                  <AurSwitch
                    disabled={showBudgetConsumptionDisabled}
                    checked={showBudgetConsumption}
                    onChange={(e) => {
                      setShowBudgetConsumption(e.target.checked);
                      changeBudgetConsumption(e.target.checked);
                    }}
                    dataTestId={'budget-consumption-check'}
                    ariaLabel={`${t('aria.toggle')} ${t(
                      'common.showEstimatedBudgetConsumption'
                    )}`}
                  />
                </Box>
              </Box>
            ) : null}
          </Can>
        </Box>
      </Box>
    </>
  );
};

export default ProjectInfoSection;
